import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import {
  changeUsername,
  selectCanChangeUsername,
  selectNextUsernameChangeAt,
  selectUsername,
} from "src/store/user"
import { playerFacingError } from "src/utils/errorMessages"
import { USERNAME_MAX_LENGTH, usernameProblem } from "src/utils/username"

import "./UsernameEditor.css"

/* The name in the profile header, and the way to change it.

   Reads as a heading until the player asks to edit it, because that is what it is for everyone who
   never will.

   **Every refusal lands under the field, in the player's language.** Renaming is a form, and the
   two things that go wrong with it — the name is malformed, or somebody has it — are both about
   the word in the box. They were toasts once, in English, stacked on top of axios's own "Request
   failed with status code 409"; a player read three lines floating over the map to learn that six
   characters were taken. Shape is answered here without asking the server at all, so it is
   answered while they type. Everything the server alone can know is asked for and shown in the
   same place.

   The field is not cleared on a refusal. A player who typed a name and was told it was taken is
   one character away from a free one, and handing them an empty box makes them type it again. */
const UsernameEditor = () => {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const username = useSelector(selectUsername)
  const canChange = useSelector(selectCanChangeUsername)
  const nextChangeAt = useSelector(selectNextUsernameChangeAt)

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState("")
  const [saving, setSaving] = useState(false)
  const [refusal, setRefusal] = useState(null)

  const wanted = draft.trim()
  const unchanged = wanted === username
  // Not shown for an empty box or a name still being typed towards valid — only once it is the
  // player's own text and still wrong. Nagging somebody mid-word is not help.
  const problem = wanted && !unchanged ? usernameProblem(wanted) : null
  const shown = refusal ?? (problem ? t(problem.key, problem.values) : null)

  const openEditor = () => {
    setDraft(username ?? "")
    setRefusal(null)
    setEditing(true)
  }

  const edit = (value) => {
    setDraft(value)
    // The server's answer was about the previous word; keeping it beside a new one is a lie.
    setRefusal(null)
  }

  const save = async (event) => {
    event.preventDefault()
    if (!wanted || unchanged || problem || saving) return

    setSaving(true)
    try {
      await dispatch(changeUsername(wanted)).unwrap()
      setEditing(false)
    } catch (rejected) {
      setRefusal(rejected?.message ?? t("errors.somethingWentWrong", { defaultValue: "Something went wrong." }))
    } finally {
      setSaving(false)
    }
  }

  const freesUpOn = nextChangeAt
    ? new Date(nextChangeAt).toLocaleDateString(i18n.language, { day: "numeric", month: "long" })
    : null

  if (!editing) {
    return (
      <div className="username-editor">
        <h2 className="profile-name">{username}</h2>
        <button
          type="button"
          className="quiet username-edit-open"
          disabled={!canChange}
          // Through `playerFacingError`, not `t`: the English of a refusal lives in
          // `errorMessages.js` (the backend parses that file), and only the Spanish is in the
          // locale — so a bare `t("errors.UsernameTooSoon")` renders the key on an English profile.
          title={canChange ? t("settings.usernameChange") : playerFacingError("UsernameTooSoon", { date: freesUpOn })}
          onClick={openEditor}
        >
          {t("settings.usernameChange")}
        </button>
      </div>
    )
  }

  return (
    <form className="username-editor editing" onSubmit={save}>
      <input
        type="text"
        className="form-input username-input"
        value={draft}
        autoFocus
        maxLength={USERNAME_MAX_LENGTH}
        aria-label={t("settings.usernameChange")}
        aria-invalid={Boolean(shown)}
        aria-describedby={shown ? "username-problem" : undefined}
        onChange={(event) => edit(event.target.value)}
        onKeyDown={(event) => event.key === "Escape" && setEditing(false)}
      />
      {shown && (
        <p className="p-mini username-problem" id="username-problem" role="alert">
          {shown}
        </p>
      )}
      <div className="username-editor-actions">
        <button type="submit" className="quiet" disabled={saving || !wanted || unchanged || Boolean(problem)}>
          {t("settings.usernameSave")}
        </button>
        <button type="button" className="quiet" onClick={() => setEditing(false)}>
          {t("settings.usernameCancel")}
        </button>
      </div>
    </form>
  )
}

export default UsernameEditor
